/**
 * Is it safe to reload the page onto a new service worker RIGHT NOW? (round 8.1)
 *
 * `registerType: 'prompt'` (vite.config.ts) leaves a newly installed worker
 * in `waiting`, and `src/ui/appUpdate.ts` is what decides when to activate
 * it — because activating it reloads the page, and a survey app that yanks
 * the page out from under a surveyor mid-keystroke would lose exactly the
 * kind of work this product exists to protect. So the reload is deliberate,
 * not automatic: it is refused while a text field is focused, while a dialog
 * is open, or while a photo capture is in flight, and the DOM half defers it
 * to the next screen change or the next time the tab goes to the background —
 * each re-checked through this predicate — and never to `focusout`, which
 * fires on every ordinary move between fields.
 *
 * The capture case is the final review's (D-040 postscript): on iOS,
 * presenting the camera or the photo picker from `<input type="file">` puts
 * the page in the background (`visibilityState === 'hidden'`), so without it
 * a deferred build would reload under the picker and the picture — and a
 * pending Retake's promise to delete the old one — would be lost.
 *
 * Pure, and kept apart from the DOM half (`src/ui/appUpdate.ts`) for the same
 * reason the rest of `src/ui/state` is: the decision is what is worth testing
 * in the node suite, and nothing here needs a real `document`.
 */

export interface ReloadContext {
  /** `document.activeElement?.tagName`, or `null` when nothing is focused. */
  activeTag: string | null;
  /** Whether a `[role="dialog"]` is open anywhere on the page right now. */
  hasOpenDialog: boolean;
  /**
   * Whether a photo capture is in flight — the OS sheet or camera opened by
   * `usePhotoCapture.open()` and not yet answered with a `change` or a
   * `cancel` (`src/ui/captureInFlight.ts`).
   */
  capturing: boolean;
}

/**
 * False while the surveyor is mid-keystroke (a focused `INPUT` or
 * `TEXTAREA`), looking at an open dialog, or out in the OS camera — each a
 * moment a reload would silently discard something they were in the middle
 * of. True otherwise.
 */
export function shouldReloadNow(context: ReloadContext): boolean {
  if (context.activeTag === 'INPUT' || context.activeTag === 'TEXTAREA') return false;
  if (context.hasOpenDialog) return false;
  if (context.capturing) return false;
  return true;
}
